import dayjs from 'dayjs'
import type { Dayjs } from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import relativeTime from 'dayjs/plugin/relativeTime'
import duration from 'dayjs/plugin/duration'

dayjs.extend(customParseFormat)
dayjs.extend(relativeTime)
dayjs.extend(duration)

export const useCustomDate = () => {
	/** @default 'DD MMM YYYY'
	 * @prop format:  'DD MMM, YYYY' | 'YYYY-MM-DD'
	 */
	const formatDate = (arg: Date | Dayjs | string, format = 'DD MMM YYYY') => (arg && dayjs(arg).format(format)) || ''

	const disableFutureYr = (current: Dayjs) => {
		const date = dayjs()
		// Disable previous days and previous years for start date and return date
		return current && date.year() < current.year()
	}
	const minDateOf2000 = (current: Dayjs) => {
		return (current && current.year() <= 1999) || disableFutureYr(current)
	}

	const disableFutureYrAndDayAfter = (current: Dayjs) => {
		const date = dayjs()
		// Disable previous days and previous years for start date and return date
		return (current && date.year() < current.year()) || current.isAfter(date)
	}
	const disablePreviousDate = (current: Dayjs, addTo = 0) => {
		// Disable previous days and previous years for start date and return date
		const date = dayjs()
		return current && (current.isBefore(date.add(addTo, 'day'), 'day') || date.year() > current.year())
	}
	const minOf18 = computed(() => {
		// Disable previous days and previous years for start date and return date
		const minimumDate = dayjs().subtract(18, 'year').endOf('day')
		return minimumDate
	})
	const isDate18Min = (current: Dayjs) => {
		return current && current > minOf18.value
	}

	const calculateAge = (dateOfBirth: string, format = 'YYYY-MM-DD') => {
		const today = dayjs()
		const birthDate = dayjs(dateOfBirth, format)

		// Calculate years
		const years = today.diff(birthDate, 'year')
		birthDate.add(years, 'year') // Subtract the full years to calculate remaining months

		// Calculate months
		const months = today.diff(birthDate, 'month')
		birthDate.add(months, 'month') // Subtract the full months to calculate remaining days

		// Calculate weeks and days
		const days = today.diff(birthDate, 'day')
		const weeks = Math.floor(days / 7)
		const remainingDays = days % 7
		let daysInLetter = ''
		if (years > 0) {
			daysInLetter = `${years} years`
		} else if (months > 0) {
			daysInLetter = `${months} months`
		} else if (weeks > 0) {
			daysInLetter = `${weeks} weeks`
		} else if (days > 0) {
			daysInLetter = `${days} days`
		}

		return {
			years,
			months,
			weeks,
			daysInLetter,
			days: remainingDays
		}
	}
	const getDateDifference = (date?: string, unit: 'hour' | 'day' = 'hour') => {
		if (!date) {
			return 0
		}
		const difference = dayjs().diff(date, unit, true)
		return Math.floor(difference)
	}
	const convertMonthToNumber = (month: string, reverse = false) => {
		const opt = {
			jan: '01',
			feb: '02',
			mar: '03',
			apr: '04',
			may: '05',
			jun: '06',
			jul: '07',
			aug: '08',
			sep: '09',
			oct: '10',
			nov: '11',
			dec: '12'
		}
		if (reverse) {
			const k = Number(Object.values(opt).findIndex(value => value === month))
			return String(Object.keys(opt).at(k))
		}
		const key = String(month).toLowerCase() as keyof typeof opt
		return String(opt[key])
	}

	const disableDateProductAgeLimit = (current: Dayjs, provider: string) => {
		const date = dayjs()
		const maxDays = getProviderAgeLimit(provider)
		return (current && date.subtract(maxDays, 'year').isAfter(current)) || disableFutureYrAndDayAfter(current)
	}

	return {
		disableDateProductAgeLimit,
		getDateDifference,
		formatDate,
		disableFutureYr,
		disableFutureYrAndDayAfter,
		disablePreviousDate,
		isDate18Min,
		minOf18,
		calculateAge,
		minDateOf2000,
		convertMonthToNumber
	}
}
